import { ApolloError } from '@apollo/client';
import { formatGraphQlError, validationErrors } from '@client/module/error/error';
import PrimaryButton from '@packages/ui/button/primary-button';
import SecondaryButton from '@packages/ui/button/secondary-button';
import Card from '@packages/ui/card/card';
import CardContent from '@packages/ui/card/card-content';
import CardFooter from '@packages/ui/card/card-footer';
import CardHeader from '@packages/ui/card/card-header';
import FormItem from '@packages/ui/form/form-item';
import FormLayout from '@packages/ui/form/form-layout';
import TextInput from '@packages/ui/form/input/text-input';
import Loading from '@packages/ui/loading';
import { useNotificationCenter } from '@packages/ui/notification/notification-center';
import { captureException } from '@sentry/browser';
import { useTranslate } from '@tolgee/react';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useUpdatePrinterDevice } from '../logic/use-update-printer-device';

interface DeviceData {
  name: string;
}

export default function PrinterDeviceUpdateModal(props: { data: { id: string; name: string }; onClose: () => void }) {
  const [update, { loading }] = useUpdatePrinterDevice();
  const { showAlert } = useNotificationCenter();
  const { t } = useTranslate();

  const {
    reset,
    control,
    setError,
    watch,
    formState: { errors },
  } = useForm<DeviceData>({
    defaultValues: {
      name: '',
    },
  });

  useEffect(() => {
    reset({
      name: props.data.name,
    });
  }, [props.data.name, reset]);

  if (loading) {
    return <Loading />;
  }

  const handleSubmit = async () => {
    try {
      const result = await update({
        variables: {
          id: props.data.id,
          data: {
            name: watch('name'),
          },
        },
      });

      if (result.data) {
        showAlert({
          status: 'success',
          title: t('common.alert.success', 'Success'),
          message: t('printer-device.alert.succes', 'Updated printer device successfully'),
        });
        props.onClose();
      }
    } catch (err) {
      if (err instanceof ApolloError) {
        const applicationErrors = formatGraphQlError(err.graphQLErrors);
        const validationError = validationErrors(applicationErrors);
        for (const field in validationError) {
          setError(
            field as keyof DeviceData,
            {
              type: 'server',
              message: t(validationError[field] ?? 'validation-error.common.alert'),
            },
            { shouldFocus: true }
          );
        }
      }
      captureException(err);
    }
  };
  return (
    <Card>
      <CardHeader title={t('printer-device.header', 'Update printer device')} />
      <CardContent>
        <FormLayout>
          <FormItem title={t('name', 'Name')}>
            <Controller
              rules={{
                required: t('printer.name.validation.required', 'Name is required'),
                maxLength: {
                  value: 100,
                  message: t('printer.update.username.validation.max', 'Name must be less than {amount} characters', {
                    amount: 100,
                  }),
                },
                minLength: {
                  value: 2,
                  message: t('printer.update.username.validation.min', 'Name must be more than {amount} characters', {
                    amount: 2,
                  }),
                },
              }}
              name={'name'}
              control={control}
              render={({ field }) => (
                <TextInput
                  data-testid="name-input"
                  type="text"
                  label="name"
                  value={field.value}
                  placeholder="Name"
                  error={errors.name?.message}
                  onChange={field.onChange}
                  name=""
                />
              )}
            />
          </FormItem>
        </FormLayout>
      </CardContent>

      <CardFooter>
        <div className="flex w-full justify-between items-center">
          <SecondaryButton onClick={props.onClose}>{t('common.button.cancel')}</SecondaryButton>
          <PrimaryButton
            onClick={() => {
              void handleSubmit();
            }}
          >
            {t('common.button.submit')}
          </PrimaryButton>
        </div>
      </CardFooter>
    </Card>
  );
}
